import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function NosVilles() {
  const navigate = useNavigate();

  const cities = [
    { name: 'Lausanne', slug: 'lausanne', region: 'Vaud', description: 'Capitale olympique, Lac Léman, centre universitaire', image: 'https://images.pexels.com/photos/34006633/pexels-photo-34006633.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Genève', slug: 'geneve', region: 'Genève', description: 'Ville internationale, organisations internationales', image: 'https://images.pexels.com/photos/36343876/pexels-photo-36343876.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Verbier', slug: 'verbier', region: 'Valais', description: 'Station de ski premium, 4 Vallées', image: 'https://images.unsplash.com/photo-1767714453178-58934c7ffd93?w=600&q=85' },
    { name: 'Montreux', slug: 'montreux', region: 'Vaud', description: 'Riviera suisse, Montreux Jazz Festival', image: 'https://images.pexels.com/photos/31579403/pexels-photo-31579403.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Crans-Montana', slug: 'crans-montana', region: 'Valais', description: 'Station alpine haut de gamme, Golf', image: 'https://images.pexels.com/photos/38015314/pexels-photo-38015314.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Neuchâtel', slug: 'neuchatel', region: 'Neuchâtel', description: 'Ville universitaire, lac, centre historique', image: 'https://images.pexels.com/photos/34875252/pexels-photo-34875252.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Villars', slug: 'villars', region: 'Vaud', description: 'Station familiale des Alpes vaudoises', image: 'https://images.unsplash.com/photo-1661381184393-6ec5c0cea4f7?w=600&q=85' },
    { name: 'Yverdon-les-Bains', slug: 'yverdon-les-bains', region: 'Vaud', description: 'Thermalisme, lac de Neuchâtel', image: 'https://images.pexels.com/photos/36732128/pexels-photo-36732128.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Fribourg', slug: 'fribourg', region: 'Fribourg', description: 'Ville bilingue, centre médiéval', image: 'https://images.pexels.com/photos/33771825/pexels-photo-33771825.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Vevey', slug: 'vevey', region: 'Vaud', description: 'Riviera vaudoise, patrimoine Chaplin', image: 'https://images.pexels.com/photos/11501893/pexels-photo-11501893.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Sion', slug: 'sion', region: 'Valais', description: 'Capitale du Valais, châteaux, vignobles', image: 'https://images.pexels.com/photos/36458834/pexels-photo-36458834.jpeg?auto=compress&cs=tinysrgb&w=600' },
    { name: 'Nyon', slug: 'nyon', region: 'Vaud', description: 'Lac Léman, festivals, proximité Genève', image: 'https://images.unsplash.com/photo-1682244069060-bf1ab1e450b7?w=600&q=85' },
    { name: 'Morges', slug: 'morges', region: 'Vaud', description: 'Lac Léman, Fête de la Tulipe', image: 'https://images.unsplash.com/photo-1765496888350-2a41bb618049?w=600&q=85' }
  ];

  return (
    <Layout>
      <SEOHead
        title="Nos Villes | Conciergerie Airbnb en Suisse Romande"
        description="Découvrez les 13 villes de Suisse romande où Homely Solutions propose ses services de conciergerie Airbnb : Lausanne, Genève, Verbier, Montreux et plus."
        canonical="https://homely-conciergerie.ch/nos-villes"
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Nos Destinations en Suisse Romande
            </h1>
            <p className="text-xl text-gray-200">
              Conciergerie Airbnb professionnelle dans 13 villes de Suisse romande
            </p>
          </div>
        </section>

        {/* Cities Grid */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cities.map((city) => (
                <Card key={city.slug} className="border-2 border-[#E5E2DB] hover:border-[#8A9474] transition-all cursor-pointer overflow-hidden group" onClick={() => navigate(`/gestion-locative/${city.slug}`)}>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={city.image} 
                      alt={`Conciergerie Airbnb ${city.name} - Gestion locative ${city.region}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      width="600"
                      height="400"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B2B]/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                      <MapPin className="h-5 w-5" />
                      <span className="font-semibold text-lg">{city.name}</span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm text-[#6B6B6B] mb-2">{city.region}</p>
                    <p className="text-[#4A4A4A] text-sm mb-4">{city.description}</p>
                    <Button 
                      variant="ghost" 
                      className="w-full text-[#8A9474] hover:text-[#7A8464] hover:bg-[#F0EFE8]"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/gestion-locative/${city.slug}`);
                      }}
                    >
                      En savoir plus <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B2B2B] mb-6">
              Votre ville n'est pas listée ?
            </h2>
            <p className="text-xl text-[#4A4A4A] mb-8">
              Contactez-nous pour discuter de nos services dans votre région
            </p>
            <Button 
              onClick={() => navigate('/estimation')}
              size="lg"
              className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold whitespace-normal sm:whitespace-nowrap"
            >
              Demander une estimation gratuite
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
